import { Crop } from 'react-image-crop';

import { NumberInputField } from './common';
import { GridRow } from './domain';
import {
    CurrencyCode,
    FreelancerServiceFragment,
    GdFreelancerProjectStatus,
    GdFreelancersCardStatusModerate,
    GdFreelancerSkillOfferDuration,
    GdFreelancerStatus,
    GdFreelancerUgcModerationStatus
} from './graphql-grid';
import { RegionSuggestElem } from './regions';

/**
 * Cписок всех возможных видов сертификатов фрилансера.
 */
export enum FreelancersCertificatesTypes {
    DIRECT_PRO = 'DIRECT_PRO',
    DIRECT = 'DIRECT',
    METRIKA = 'METRIKA',
    RSYA = 'RSYA'
}

export const FREELANCER_CERTIFICATE_TYPES_ORDER = [
    FreelancersCertificatesTypes.DIRECT_PRO,
    FreelancersCertificatesTypes.DIRECT,
    FreelancersCertificatesTypes.METRIKA,
    FreelancersCertificatesTypes.RSYA
];

export const FREELANCER_SERVICE_DURATION_TYPES_ORDER = [
    GdFreelancerSkillOfferDuration.FROM_1_TO_3_DAYS,
    GdFreelancerSkillOfferDuration.FROM_3_TO_7_DAYS,
    GdFreelancerSkillOfferDuration.FROM_7_TO_14_DAYS,
    GdFreelancerSkillOfferDuration.FROM_14_TO_28_DAYS,
    GdFreelancerSkillOfferDuration.FROM_1_TO_3_MONTHS,
    GdFreelancerSkillOfferDuration.MORE_THAN_3_MONTHS,
    GdFreelancerSkillOfferDuration.MONTHLY
];

/**
 * Список состояний страницы
 */

export enum FreelancerPageStates {
    PENDING = 'PENDING',
    LOADED = 'LOADED',
    ERROR = 'ERROR',
    NO_ACCESS = 'NO_ACCESS',
    NOT_FOUND = 'NOT_FOUND'
}

export interface FreelancersCertificates {
    type: FreelancersCertificatesTypes;
    id: string;
    link: string;
}

export enum FreelancerMandatoryContactTypes {
    PHONE = 'PHONE',
    MAIL = 'MAIL'
}

export const FreelancerMandatoryContactTypesOrder = [
    FreelancerMandatoryContactTypes.PHONE,
    FreelancerMandatoryContactTypes.MAIL
];

export enum FreelancerOptionalContactTypes {
    ICQ = 'ICQ',
    WHATSAPP = 'WHATSAPP',
    TELEGRAM = 'TELEGRAM',
    WEBSITE = 'WEBSITE',
    SKYPE = 'SKYPE',
    VIBER = 'VIBER'
}

export const FreelancerOptionalContactTypesOrder = [
    FreelancerOptionalContactTypes.ICQ,
    FreelancerOptionalContactTypes.WHATSAPP,
    FreelancerOptionalContactTypes.TELEGRAM,
    FreelancerOptionalContactTypes.WEBSITE,
    FreelancerOptionalContactTypes.SKYPE,
    FreelancerOptionalContactTypes.VIBER
];

export type FreelancerContactTypes = FreelancerMandatoryContactTypes | FreelancerOptionalContactTypes;

export interface FreelancerContacts {
    [FreelancerMandatoryContactTypes.PHONE]: string;
    [FreelancerMandatoryContactTypes.MAIL]: string;
    [FreelancerOptionalContactTypes.ICQ]?: string;
    [FreelancerOptionalContactTypes.WHATSAPP]?: string;
    [FreelancerOptionalContactTypes.TELEGRAM]?: string;
    [FreelancerOptionalContactTypes.WEBSITE]?: string;
    [FreelancerOptionalContactTypes.SKYPE]?: string;
    [FreelancerOptionalContactTypes.VIBER]?: string;
}

export interface FreelancerContactInputs {
    [FreelancerMandatoryContactTypes.PHONE]: StringInputField;
    [FreelancerMandatoryContactTypes.MAIL]: StringInputField;
    [FreelancerOptionalContactTypes.ICQ]?: StringInputField;
    [FreelancerOptionalContactTypes.WHATSAPP]?: StringInputField;
    [FreelancerOptionalContactTypes.TELEGRAM]?: StringInputField;
    [FreelancerOptionalContactTypes.WEBSITE]?: StringInputField;
    [FreelancerOptionalContactTypes.SKYPE]?: StringInputField;
    [FreelancerOptionalContactTypes.VIBER]?: StringInputField;
}

export interface FreelancerProject {
    projectId: string;
    ownFreelancerId: string;
    ownFreelancerLogin: string;
    clientStatus: GdFreelancerProjectStatus;
    createdTime: string;
    startedTime?: string;
}

export interface FreelancerCustomer {
    name: string;
    avatarLink: string;
    phone?: string;
    isDirectOwnCustomer: boolean;
    freelancerProject?: FreelancerProject;
    hasCooperation: boolean;
}

interface FreelancerCommon {
    freelancerId: string;
    login: string;
    avatarLink?: string;
    fullName: string;
    region: FreelancerCardInitRegion;
    about?: string;
    freelancerScore: number;
    workCurrency: CurrencyCode;
    status: GdFreelancerStatus;
    hasPublicCard: boolean;
}

export interface FreelancerCardOperator {
    login?: string;
    clientId?: string;
}

export interface FreelancerQualityParams {
    qualityRank: number;
    qualityScore: number;
    totalFreelancersCount: number;
}

export type Freelancer = FreelancerCommon & {
    cardModerationStatus?: GdFreelancersCardStatusModerate;
    certificates: FreelancersCertificates[];
    contacts?: FreelancerContacts;
    mainSkill: FreelancerSkills;
    skills: FreelancerSkills[];
    freelancerFeedbackItems: FreelancerFeedbackItem[];
    quality?: FreelancerQualityParams;
};

export type FreelancersListItem = FreelancerCommon & {
    isCurrentFreelancer: boolean;
    services: string[];
    feedbackCount: number;
    approximateDuration: string;
    approximatePrice: number;
    statusModerate: GdFreelancersCardStatusModerate;
};

export interface FreelancersList {
    freelancersListItems: FreelancersListItem[];
    pageStatus: FreelancerPageStates;
}

export interface DurationInputField {
    inputVal: GdFreelancerSkillOfferDuration;
    error?: string;
}

export interface FreelancerServiceInput {
    skillId: string;
    price: NumberInputField;
    duration: DurationInputField;
}

export interface FreelancerSkills {
    skillName: string;
    price: number;
    duration: GdFreelancerSkillOfferDuration;
}

export enum PartnerRole {
    FREELANCER = 'freelancer',
    CUSTOMER = 'customer'
}

export enum FreelancerCustomersTabs {
    ACTIVE = 0,
    CANCELED = 1,
    REQUESTED = 2
}

export type FreelancerCustomersActions = {
    acceptCustomerProject: (clientId: string, projectId: string) => void;
    declineCustomerProject: (clientId: string, projectId: string) => void;
    openDeclineProjectPopup: (clientId: string, projectId: string) => void;
};

export enum FreelancerCustomersScreenStage {
    INITIAL = 'initial',
    LOADING = 'loading',
    READY = 'ready',
    ERROR = 'error',
    NO_ACCESS = 'no-access'
}

export interface FreelancerCustomersState {
    tab: FreelancerCustomersTabs;
    stage: FreelancerCustomersScreenStage;
    active: FreelancerCustomersListItem[];
    canceled: FreelancerCustomersListItem[];
    requested: FreelancerCustomersListItem[];
    declinePopupIsOpen: boolean;
    declinePopupIsDisabled: boolean;
    declinePopupClientId: string;
    declinePopupProjectId: string;
}

export interface FreelancerCustomersData {
    active: FreelancerCustomersListItem[];
    canceled: FreelancerCustomersListItem[];
    requested: FreelancerCustomersListItem[];
}

export interface FreelancerCustomersListItem extends GridRow {
    fio: string;
    clientId: string;
    projectId: string;
    chiefLogin: string;
    phoneNumber?: string;

    status: GdFreelancerProjectStatus;
    createdTime: string;
    updatedTime: string;
    startedTime?: string;

    isDisabled: boolean;
}

export interface UpdateCustomerRequestData {
    isAccepted: boolean;
    projectId: string;
    updatedTime: string;
}

export interface UpdateActiveProjectData {
    projectId: string;
    updatedTime: string;
}

export enum FreelancerFeedbackFormModes {
    CLOSE_COOPERATION = 'CLOSE_COOPERATION',
    ADD_NEW_FEEDBACK = 'ADD_NEW_FEEDBACK',
    EDIT_FEEDBACK = 'EDIT_FEEDBACK'
}

export interface FreelancerFeedbackItem extends FreelancerFeedbackCommonData {
    id: string;
    partnershipDuration: string;
    creationTime: string;
    customer?: FreelancerFeedbackUserData;
    authorId: string;
    moderationStatus: GdFreelancerUgcModerationStatus;
}

export enum FreelancerFeedbackFlavor {
    READONLY = 'READONLY', // пользователь не является фрилансером, про которого оставлен отзыв и не является автором отзыва
    EDITABLE = 'EDITABLE' // пользователь является автором отзыва
}

export interface FreelancerFeedbackUserData {
    name: string;
    avatar: string;
    login: string;
}

export interface FreelancerFeedbackBody {
    body: string;
    creationTime: string;
}

export interface FreelancerFeedbackCommonData {
    willRecommend: boolean;
    freelancerScore: number;
    feedbackText: string;
}

export interface StringInputField {
    inputVal: string;
    error?: StringInputFieldErrors;
    focused?: boolean;
}

export enum StringInputFieldErrors {
    MANDATORY_FIELD = 'MANDATORY_FIELD',
    INCORRECT_FORMAT = 'INCORRECT_FORMAT'
}

export enum FreelancerInfoPanelClickTypes {
    FREELANCER = 'FREELANCER',
    MY_PROFILE = 'MY_PROFILE',
    MY_CUSTOMERS = 'MY_CUSTOMERS',
    ALL_SPECIALISTS_BY_FREELANCER = 'ALL_SPECIALISTS_BY_FREELANCER',
    ALL_SPECIALISTS_BY_CUSTOMER = 'ALL_SPECIALISTS_BY_CUSTOMER',
    BECOME_SPECIALIST = 'BECOME_SPECIALIST',
    MY_OWN_CAMPAIGNS = 'MY_OWN_CAMPAIGNS'
}

export interface FreelancerCardEditData {
    avatarUrl: string;
    fullName?: string;
    registrationDate: string;
    briefInfo: StringInputField;
    contacts: FreelancerContactInputs;
    services: FreelancerServiceInput[];
}

export interface FreelancerCardEditState {
    init: FreelancerCardEditData;
    edit: FreelancerCardEditData;
    certificates: FreelancersCertificates[];
    services: FreelancerServiceFragment[];
    serviceDurationTypes: GdFreelancerSkillOfferDuration[];
    currencyCode: CurrencyCode;
    isDataReadyToSave: boolean;
    pageStatus: FreelancerPageStates;
    isImagePopupOpen: boolean;
    isImageUploading: boolean;
    isLocalImageUploading: boolean;
    imagePopupData?: FreelancerCardEditImageData;
    imagePopupCrop: Crop;
    messageError?: string;
    countAttachImages: number;
    initRegion: FreelancerCardInitRegion;
    avatarId?: string;
    isSavingInProgress: boolean;
}

export interface FreelancerCardEditImageData {
    width: number;
    height: number;
    urlBase: string;
    name: string;
}

export interface FreelancerCardInitRegion {
    geoId?: number;
    region: string;
    country: string;
}

export interface FreelancerCardEditRegion {
    geoId?: number;
    text: string;
    suggestList: RegionSuggestElem[];
    hasError: boolean;
}

export enum FreelancerCardEditValidationError {
    MANDATORY_FIELD = 'mandatory-field',
    INCORRECT_FORMAT = 'incorrect-format'
}

export interface FreelancerAvatar {
    id: string;
    url: string;
}
